import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Mail, Loader2, RefreshCw, Trash2, Clock } from "lucide-react";
import { invitesApi } from "@/lib/api";

// PendingInvitesSection — Jul 2026.
//
// Renders the list of pending (unused) invites: workspace-wide or scoped to
// a specific project. Each row shows the invitee's email, role, when it was
// sent, expiry countdown, plus Resend + Revoke actions. Silent when the list
// is empty so admin pages don't grow useless tombstones on quiet workspaces.
//
// Prop `projectId` (optional) filters the fetch — omit for the workspace-wide
// list shown on AdminUsersPage.
export default function PendingInvitesSection({ projectId = null, title = null }) {
  const [invites, setInvites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);

  const reload = () => {
    setLoading(true);
    invitesApi
      .list(projectId)
      .then(setInvites)
      .catch(() => toast.error("Failed to load pending invites"))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    reload();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId]);

  const handleResend = async (inv) => {
    setBusyId(inv.id);
    try {
      await invitesApi.resend(inv.id);
      toast.success(`Re-sent invite to ${inv.email}`);
      reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to resend");
    } finally {
      setBusyId(null);
    }
  };

  const handleRevoke = async (inv) => {
    if (!window.confirm(`Revoke pending invite for ${inv.email}?`)) return;
    setBusyId(inv.id);
    try {
      await invitesApi.revoke(inv.id);
      toast.success(`Revoked invite for ${inv.email}`);
      reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to revoke");
    } finally {
      setBusyId(null);
    }
  };

  if (loading) {
    return (
      <section
        data-testid="pending-invites-loading"
        className="mb-8 flex items-center gap-2 text-zinc-500 text-sm px-1"
      >
        <Loader2 className="w-4 h-4 animate-spin" /> Loading pending invites…
      </section>
    );
  }

  // Empty state — hide the section entirely.
  if (!invites || invites.length === 0) return null;

  const expiredCount = invites.filter((i) => i.is_expired).length;

  return (
    <section
      data-testid="pending-invites-section"
      className="mb-8 border border-amber-500/20 bg-amber-500/5 rounded-md overflow-hidden"
    >
      <header className="px-4 py-3 border-b border-amber-500/20 flex items-center gap-2 flex-wrap">
        <Mail className="w-4 h-4 text-amber-400 shrink-0" />
        <h3 className="font-heading text-lg font-light text-zinc-100">
          {title || "Pending Invites"}
        </h3>
        <span className="text-[10px] font-mono uppercase tracking-wide px-1.5 py-0.5 rounded-sm border border-amber-500/40 bg-amber-500/10 text-amber-300">
          {invites.length}
        </span>
        {expiredCount > 0 && (
          <span
            data-testid="pending-invites-expired-count"
            className="text-[10px] font-mono uppercase tracking-wide px-1.5 py-0.5 rounded-sm border border-rose-500/40 bg-rose-500/10 text-rose-300"
          >
            {expiredCount} expired
          </span>
        )}
        <p className="text-xs text-zinc-500 ml-auto">
          These people haven&apos;t accepted yet. Resend or revoke.
        </p>
      </header>

      <ul className="divide-y divide-zinc-800/60">
        {invites.map((inv) => {
          const busy = busyId === inv.id;
          const expiryLabel = inv.is_expired
            ? "Expired"
            : inv.days_until_expiry === 0
            ? "Expires today"
            : inv.days_until_expiry === 1
            ? "Expires tomorrow"
            : `Expires in ${inv.days_until_expiry}d`;
          return (
            <li
              key={inv.id}
              data-testid={`pending-invite-row-${inv.email}`}
              className="flex items-center gap-3 px-4 py-3 hover:bg-amber-500/[0.04] transition-colors"
            >
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-sm text-zinc-100 truncate">
                    {inv.email}
                  </span>
                  <span className="inline-flex items-center px-1.5 py-0.5 rounded-sm text-[9px] font-mono uppercase tracking-wide border border-amber-500/40 bg-amber-500/10 text-amber-300 shrink-0">
                    Pending
                  </span>
                  <span className="inline-flex items-center px-1.5 py-0.5 rounded-sm text-[9px] font-mono uppercase tracking-wide border border-zinc-700 bg-zinc-900/60 text-zinc-400 shrink-0">
                    {inv.role}
                  </span>
                  {inv.project_name && !projectId && (
                    <span className="text-[11px] text-zinc-500 truncate">
                      · {inv.project_name}
                    </span>
                  )}
                </div>
                <div className="text-[10px] text-zinc-500 font-mono mt-0.5 flex items-center gap-2 flex-wrap">
                  <span
                    className={
                      inv.is_expired ? "text-rose-400" : "text-zinc-500"
                    }
                  >
                    <Clock className="inline w-2.5 h-2.5 mr-1" />
                    {expiryLabel}
                  </span>
                  <span className="text-zinc-700">·</span>
                  <span>
                    Sent {inv.age_days === 0 ? "today" : `${inv.age_days}d ago`}
                    {inv.invited_by_name ? ` by ${inv.invited_by_name}` : ""}
                  </span>
                </div>
              </div>
              <button
                type="button"
                data-testid={`pending-invite-resend-${inv.email}`}
                onClick={() => handleResend(inv)}
                disabled={busy}
                className="inline-flex items-center gap-1.5 px-2.5 py-1 text-[11px] font-mono uppercase tracking-wide rounded-sm border border-zinc-800 text-zinc-300 hover:border-amber-500/60 hover:text-amber-400 hover:bg-amber-500/10 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
              >
                {busy ? (
                  <Loader2 className="w-3 h-3 animate-spin" />
                ) : (
                  <RefreshCw className="w-3 h-3" />
                )}
                Resend
              </button>
              <button
                type="button"
                data-testid={`pending-invite-revoke-${inv.email}`}
                onClick={() => handleRevoke(inv)}
                disabled={busy}
                title="Revoke pending invite"
                className="p-1.5 rounded-sm text-zinc-600 hover:text-rose-400 hover:bg-rose-500/10 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
              >
                <Trash2 className="w-3.5 h-3.5" />
              </button>
            </li>
          );
        })}
      </ul>
    </section>
  );
}
